import { googleRecaptchaSiteKey } from '~/config/public/environment';
import { requestErrorHandler } from './notifyError';

export const RECAPTCHA_TOKEN_HEADER = 'X-Recaptcha-Token';

export const runWithRecaptcha = (func: (googleResponse: string) => void) => {
  if (
    typeof window !== 'undefined' &&
    typeof window.grecaptcha !== 'undefined'
  ) {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(googleRecaptchaSiteKey, {
          action: 'submit'
        })
        .then(googleResponse => {
          func(googleResponse);
        })
        .catch(requestErrorHandler);
    });
  } else {
    console.error(new Error('No recaptcha enabled.'));
  }
};

const recaptchaLoadTimeout = 5000;
const recaptchaPollInterval = 100;

const waitForRecaptcha = () =>
  new Promise<void>((resolve, reject) => {
    const noRecaptcha = new Error('No recaptcha enabled.');

    if (typeof window === 'undefined') {
      reject(noRecaptcha);
      return;
    }

    if (typeof window.grecaptcha !== 'undefined') {
      resolve();
      return;
    }

    const deadline = Date.now() + recaptchaLoadTimeout;
    const intervalId = setInterval(() => {
      if (typeof window.grecaptcha !== 'undefined') {
        clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() >= deadline) {
        clearInterval(intervalId);
        reject(noRecaptcha);
      }
    }, recaptchaPollInterval);
  });

const waitForRecaptchaReady = () =>
  new Promise<void>(resolve => window.grecaptcha.ready(() => resolve()));

export const getRecaptchaToken = async () => {
  try {
    await waitForRecaptcha();
  } catch (error) {
    console.error(error);
    throw error;
  }

  await waitForRecaptchaReady();

  try {
    return await window.grecaptcha.execute(googleRecaptchaSiteKey, {
      action: 'submit'
    });
  } catch (error) {
    requestErrorHandler(error);
    throw error;
  }
};

export const getRecaptchaHeaders = async () => {
  try {
    return { [RECAPTCHA_TOKEN_HEADER]: await getRecaptchaToken() };
  } catch {
    return {};
  }
};
